import { useEffect, useState } from 'react';

import { getApiClient } from '@apps/web/lib/api_client';
import { useMode } from '@apps/web/lib/mode_store';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import {
    type ApiError,
    type ApiResponseType,
    isAbortError,
} from '@libraries/typescript/api_client';

export function useCoverageMapPoints({
    organizationId,
    enabled,
    includeInactive,
}: {
    readonly organizationId: string | null;
    readonly enabled: boolean;
    readonly includeInactive: boolean;
}) {
    return useApiQuery({
        path: '/organizations/{organization_id}/coverage/map',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            ...(includeInactive ? { include_inactive: true } : {}),
        },
        enabled: enabled && !!organizationId,
        select: (response) => response,
        reason: 'coverage-map',
    });
}

type ClientDensityResponse = ApiResponseType<
    '/organizations/{organization_id}/coverage/map/client-density',
    'get'
>;

// The density response is still business-sensitive. Keep it outside SWR so
// disabling the layer aborts an in-flight request and releases the response.
export function useCoverageMapClientDensity({
    organizationId,
    enabled,
}: {
    readonly organizationId: string | null;
    readonly enabled: boolean;
}) {
    const apiClient = getApiClient();
    const mode = useMode();
    const [data, setData] = useState<ClientDensityResponse>();
    const [error, setError] = useState<ApiError>();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let active = true;
        setData(undefined);
        setError(undefined);
        if (!enabled || !organizationId) {
            setIsLoading(false);
            return;
        }

        const controller = new AbortController();
        setIsLoading(true);
        void apiClient
            .get({
                url: '/organizations/{organization_id}/coverage/map/client-density',
                pathParams: { organization_id: organizationId },
                mode,
                reason: 'coverage-map-client-density',
                signal: controller.signal,
            })
            .then((response) => {
                if (active) {
                    setData(response);
                    setIsLoading(false);
                }
            })
            .catch((requestError: unknown) => {
                if (!active || isAbortError(requestError)) {
                    return;
                }
                setError(requestError as ApiError);
                setIsLoading(false);
            });

        return () => {
            active = false;
            controller.abort();
        };
    }, [apiClient, enabled, mode, organizationId]);

    return { data, error, isLoading, isValidating: isLoading };
}

export function useCoverageAvailability({
    organizationId,
    weekStart,
    includeInactive,
}: {
    readonly organizationId: string | null;
    readonly weekStart: string | undefined;
    readonly includeInactive: boolean;
}) {
    return useApiQuery({
        path: '/organizations/{organization_id}/coverage',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            ...(weekStart ? { week_start: weekStart } : {}),
            ...(includeInactive ? { include_inactive: true } : {}),
        },
        enabled: !!organizationId,
        select: (response) => response,
        reason: 'coverage-dashboard',
    });
}
